import { set } from 'react-hook-form';
import type { ClientRole, PatchRolePermissionPayload } from '@/api/roles-and-permission';
import type { GroupedPermission } from '@/types/Permission';
import { LIST_LOTTERIES_PERMISSION, LOTTERY_PERMISSION_GROUP } from '@/utils/lotteryAccess';

export type TProductGroupType = 'TERMINAL' | 'SHERPA' | 'ROVER';
export { LIST_LOTTERIES_PERMISSION, LOTTERY_PERMISSION_GROUP };

type PermissionVisibilityOptions = {
  canManageLotteryPrivileges?: boolean;
};

export const permissionGroupMapping: { [k in TProductGroupType]: string[] } = {
  TERMINAL: [
    'User Management',
    'Management of Roles & Privileges',
    'Client',
    'Title',
    'Vendor',
    'Widget',
    'Dashboard',
    'Asset',
    'Approval',
    'Category',
    'Company',
    'Lottery',
  ],
  SHERPA: ['Project', 'Project Template', 'TDM', 'TDM Comments', 'Phase'],
  ROVER: ['Application Form', 'Application', 'Partner Proposal', 'Client Application'],
};

export const findPermissionGroupByName = (name: string) => {
  if (permissionGroupMapping.TERMINAL.includes(name)) {
    return 'Terminal';
  }

  if (permissionGroupMapping.SHERPA.includes(name)) {
    return 'Sherpa';
  }

  if (permissionGroupMapping.ROVER.includes(name)) {
    return 'Rover';
  }

  return 'unknown';
};

export function getPermissionGroupsForTab(currentTab: TProductGroupType, options?: PermissionVisibilityOptions) {
  const groups = permissionGroupMapping[currentTab].filter((group) => group !== 'Client');

  if (options?.canManageLotteryPrivileges === false) {
    return groups.filter((group) => group !== LOTTERY_PERMISSION_GROUP);
  }

  return groups;
}

export type PrevilegesForm = Record<string, PrevilegeGroup>;
type PrevilegeGroup = Record<string, Previleges>;
type Previleges = Record<string, boolean>;

export const convertPermissionsToFormState = (data: GroupedPermission[], clientRoles: ClientRole[]) => {
  const allChecked: PrevilegesForm = {};
  const allUnchecked: PrevilegesForm = {};
  const initialState: PrevilegesForm = {};

  data.forEach((clientRole, index) => {
    const checkedCount: Record<TProductGroupType, number> = {
      ROVER: 0,
      SHERPA: 0,
      TERMINAL: 0,
    };
    const totalCount: Record<TProductGroupType, number> = {
      ROVER: 0,
      SHERPA: 0,
      TERMINAL: 0,
    };

    const permissionGroup: PrevilegeGroup = {};

    Object.getOwnPropertyNames(clientRole).forEach((groupKey) => {
      const groupedPermissions = clientRole[groupKey as keyof GroupedPermission] || [];

      let permissions: Previleges = {};

      groupedPermissions.forEach((current) => {
        permissions = { ...permissions, [current.slug]: current.allow };

        // Create reference object where all permissions checked
        set(allChecked, `${clientRoles[index].slug}.${groupKey}.${current.slug}`, true);

        // Create reference object where all permissions unchecked
        set(allUnchecked, `${clientRoles[index].slug}.${groupKey}.${current.slug}`, false);

        // Find to which category (TERMINAL, SHERPA, ROVER) permission_group belongs to
        const tabName = findPermissionGroupByName(groupKey).toUpperCase() as TProductGroupType;

        if (current.allow) {
          checkedCount[tabName]++;
        }

        totalCount[tabName]++;
      });

      permissionGroup[groupKey] = permissions;
    });

    initialState[clientRoles[index].slug] = permissionGroup;

    // Update all checkbox state for each tab
    initialState[clientRoles[index].slug].all = {
      TERMINAL: checkedCount.TERMINAL === totalCount.TERMINAL,
      ROVER: checkedCount.ROVER === totalCount.ROVER,
      SHERPA: checkedCount.SHERPA === totalCount.SHERPA,
    };
  });

  return { initialState, allChecked, allUnchecked };
};

export const createPatchPayload = (dirtyFields: PrevilegesForm) => {
  const payloads: PatchRolePermissionPayload[] = Object.keys(dirtyFields).map((role) => {
    const dirtyFieldValues = Object.values(dirtyFields[role]);
    let groupPrivileges: Record<string, boolean> = {};

    dirtyFieldValues.forEach((group, index) => {
      // Omit all checkbox state
      if (Object.keys(dirtyFields[role])[index] === 'all') {
        return;
      }

      groupPrivileges = { ...groupPrivileges, ...group };
    });

    const privilegesArray = Object.keys(groupPrivileges).map((privilegeSlug) => ({
      slug: privilegeSlug,
      allow: groupPrivileges[privilegeSlug],
    }));

    return {
      role,
      privileges: privilegesArray,
    };
  });

  return payloads;
};

export function stripRestrictedPrivilegesFromPayloads(payloads: PatchRolePermissionPayload[], options?: PermissionVisibilityOptions) {
  if (options?.canManageLotteryPrivileges !== false) {
    return payloads;
  }

  return payloads.map((payload) => ({
    ...payload,
    privileges: payload.privileges.filter((privilege) => privilege.slug !== LIST_LOTTERIES_PERMISSION),
  }));
}

// Get all permissions that belongs to active selected tab
export const getTabPermissions = (currentTab: TProductGroupType, previlegeGroup: PrevilegeGroup, options?: PermissionVisibilityOptions) => {
  const allowedGroups = getPermissionGroupsForTab(currentTab, options);

  return Object.fromEntries(Object.entries(previlegeGroup).filter(([groupKey]) => allowedGroups.includes(groupKey)));
};

// Check if all permissions are enabled in specific user role
export const isAllChecked = (
  currentTab: TProductGroupType,
  roleSlug: string,
  allPermissions: PrevilegesForm,
  options?: PermissionVisibilityOptions
): boolean => {
  const permissinos = getTabPermissions(currentTab, allPermissions[roleSlug], options);

  return Object.keys(permissinos)
    .filter((groupKey) => groupKey !== 'all')
    .every((groupKey) => Object.values(permissinos[groupKey]).every((permission) => permission === true));
};
